import { Animated, Image, Platform, Pressable, StyleSheet, View } from "react-native";

import { Text } from "@/design/Text";
import { AirPlayIcon, CastIcon, NextIcon, PauseIcon, PlayIcon, PreviousIcon } from "@/design/icons";
import { palette, radius, space } from "@/design/tokens";
import { CAST_MINI_PILL_HEIGHT } from "@/playback/cast/castMiniLayout";
import { useSmoothPlaybackProgress } from "@/playback/useSmoothPlaybackProgress";

export type CastMiniPillProps = {
  artworkUrl: string | null;
  currentTime: number;
  deviceName: string;
  duration: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onBody: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onTogglePlay: () => void;
  itemKey: string;
  playing: boolean;
  route: "airplay" | "cast";
  title: string;
};

export function CastMiniPill({
  artworkUrl,
  currentTime,
  deviceName,
  duration,
  hasNext,
  hasPrevious,
  onBody,
  onNext,
  onPrevious,
  onTogglePlay,
  itemKey,
  playing,
  route,
  title
}: CastMiniPillProps) {
  const progressAnimation = useSmoothPlaybackProgress({
    currentTime,
    duration,
    itemKey,
    mode: "glide",
    playing,
    sampleIntervalMs: route === "cast" ? 1000 : 500
  });

  return (
    <View
      accessibilityLabel={`Playing ${title} on ${deviceName}`}
      style={styles.shell}
    >
      <Pressable
        accessibilityHint="Returns to the full player"
        accessibilityLabel={`Now playing ${title}`}
        accessibilityRole="button"
        onPress={onBody}
        style={({ hovered, pressed }) => [
          styles.content,
          hovered && styles.contentHovered,
          pressed && styles.pressed
        ]}
      >
        {artworkUrl ? (
          <Image
            onError={() => {
              console.log(`[mini-player] artwork load failed item=${itemKey}`);
            }}
            onLoad={() => {
              console.log(`[mini-player] artwork loaded item=${itemKey}`);
            }}
            source={{ uri: artworkUrl }}
            style={styles.artwork}
          />
        ) : (
          <View style={[styles.artwork, styles.artworkFallback]}>
            {route === "airplay" ? (
              <AirPlayIcon color={palette.accent} size={18} />
            ) : (
              <CastIcon color={palette.accent} size={18} />
            )}
          </View>
        )}
        <View style={styles.copy}>
          {/* Device name alone — the cast glyphs already say "casting", and
              the name is the part that must survive narrow-width truncation. */}
          <Text color={palette.accent} numberOfLines={1} style={styles.device} variant="overline">
            {deviceName}
          </Text>
          <Text numberOfLines={1} variant="smallStrong">
            {title}
          </Text>
        </View>
      </Pressable>

      {/* Previous · play/pause · next — "go back, that was Emma!" is the
          living-room moment this pill exists for. Stop-casting lives one tap
          deeper (body → full player), matching its once-per-session rarity. */}
      <Pressable
        accessibilityLabel="Play previous dance"
        accessibilityRole="button"
        disabled={!hasPrevious}
        hitSlop={6}
        onPress={onPrevious}
        style={({ hovered, pressed }) => [
          styles.control,
          !hasPrevious && styles.disabled,
          hovered && hasPrevious && styles.controlHovered,
          pressed && styles.pressed
        ]}
      >
        <PreviousIcon color={palette.textSoft} size={18} />
      </Pressable>
      <Pressable
        accessibilityLabel={playing ? "Pause" : "Play"}
        accessibilityRole="button"
        hitSlop={6}
        onPress={onTogglePlay}
        style={({ hovered, pressed }) => [
          styles.control,
          hovered && styles.controlHovered,
          pressed && styles.pressed
        ]}
      >
        {playing ? (
          <PauseIcon color={palette.text} size={24} />
        ) : (
          <PlayIcon color={palette.text} size={24} />
        )}
      </Pressable>
      <Pressable
        accessibilityLabel="Play next dance"
        accessibilityRole="button"
        disabled={!hasNext}
        hitSlop={6}
        onPress={onNext}
        style={({ hovered, pressed }) => [
          styles.control,
          !hasNext && styles.disabled,
          hovered && hasNext && styles.controlHovered,
          pressed && styles.pressed
        ]}
      >
        <NextIcon color={palette.textSoft} size={18} />
      </Pressable>

      {duration > 0 ? (
        <View style={[styles.progressTrack, { pointerEvents: "none" }]}>
          <Animated.View
            style={[
              styles.progressFill,
              {
                width: progressAnimation.interpolate({
                  inputRange: [0, 1],
                  outputRange: ["0%", "100%"]
                })
              }
            ]}
          />
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    alignItems: "center",
    alignSelf: "center",
    backgroundColor: palette.surfaceOverlay,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.lg,
    borderWidth: 1,
    flexDirection: "row",
    maxWidth: 560,
    minHeight: CAST_MINI_PILL_HEIGHT,
    overflow: "hidden",
    paddingRight: space.xs,
    ...Platform.select({
      native: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.35,
        shadowRadius: 20
      },
      web: { boxShadow: "0 8px 20px rgba(0, 0, 0, 0.35)" }
    }),
    width: "100%"
  },
  content: {
    alignItems: "center",
    flex: 1,
    flexDirection: "row",
    gap: space.md,
    minWidth: 0,
    padding: space.sm
  },
  contentHovered: {
    backgroundColor: palette.coolWashSubtle
  },
  artwork: {
    backgroundColor: palette.surfaceSunken,
    borderRadius: radius.md,
    height: 50,
    width: 72
  },
  artworkFallback: {
    alignItems: "center",
    justifyContent: "center"
  },
  copy: {
    flex: 1,
    minWidth: 0
  },
  device: {
    marginBottom: 2
  },
  control: {
    alignItems: "center",
    borderRadius: radius.pill,
    height: 44,
    justifyContent: "center",
    width: 44
  },
  controlHovered: {
    backgroundColor: palette.coolWashHover
  },
  disabled: {
    opacity: 0.3
  },
  pressed: {
    opacity: 0.7
  },
  progressTrack: {
    backgroundColor: "transparent",
    bottom: 0,
    height: 2,
    left: 0,
    position: "absolute",
    right: 0
  },
  progressFill: {
    backgroundColor: palette.accent,
    height: "100%",
    opacity: 0.9
  }
});
